import { useState, useEffect, useRef } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Lightbulb,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  Plus,
  ExternalLink,
  User,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getCategoryInfo, FACT_CATEGORIES } from "@/components/AchievementFacts";
import { FormattedText, FormattingToolbar } from "@/components/ui/formatted-text";

const API = `${getApiUrl()}/api`;

/**
 * Pending Fact Submissions Panel
 * Shows submissions awaiting admin review
 */
export function PendingFactSubmissions({ onRefresh }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewNote, setReviewNote] = useState("");

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/achievement-facts/admin/submissions?status=pending`);
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
      toast.error("Failed to load fact submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (submissionId, action) => {
    setReviewingId(submissionId);
    try {
      await axios.post(`${API}/achievement-facts/admin/submissions/${submissionId}/review`, {
        action,
        reviewNote: reviewNote || null,
      });
      toast.success(action === "approve" ? "Fact approved!" : "Fact rejected");
      setReviewNote("");
      fetchSubmissions();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Failed to review submission:", error);
      toast.error("Failed to process review");
    } finally {
      setReviewingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="text-center py-8">
        <Lightbulb className="w-10 h-10 mx-auto text-white/20 mb-3" />
        <p className="text-white/40">No pending fact submissions</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Lightbulb className="w-5 h-5 text-amber-400" />
        Pending Fact Reviews ({submissions.length})
      </h3>
      
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {submissions.map((submission) => {
          const category = getCategoryInfo(submission.category);
          const isReviewing = reviewingId === submission.id;
          
          return (
            <div
              key={submission.id}
              className="bg-black/30 rounded-lg p-4 border border-white/10"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm text-white/50 mb-2">
                    <span className="font-medium text-white/70">{submission.gameName}</span>
                    <span>•</span>
                    <span>{submission.achievementName}</span>
                  </div>
                  
                  <FormattedText text={submission.factText} className="text-white/90 mb-2" />
                  
                  <div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
                    <span className="flex items-center gap-1">
                      <span>{category.icon}</span>
                      {category.label}
                    </span>
                    {submission.sourceUrl && (
                      <a
                        href={submission.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-white/60"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Source
                      </a>
                    )}
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {submission.submittedByUsername}
                      {submission.showSubmitter && " (wants credit)"}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleReview(submission.id, "approve")}
                    disabled={isReviewing}
                    className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
                  >
                    {isReviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleReview(submission.id, "reject")}
                    disabled={isReviewing}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Achievement Facts Manager
 * Admin can add/edit/delete facts for achievements
 */
export function AchievementFactsManager({ 
  achievementId, 
  achievementName,
  masterGameId,
  gameName,
  onClose 
}) {
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingFact, setEditingFact] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [factText, setFactText] = useState("");
  const [category, setCategory] = useState("fun_fact");
  const [sourceUrl, setSourceUrl] = useState("");
  const factTextareaRef = useRef(null);

  useEffect(() => {
    fetchFacts();
  }, [achievementId]);

  const fetchFacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/achievement-facts/achievement/${achievementId}`);
      setFacts(response.data.facts || []);
    } catch (error) {
      console.error("Failed to fetch facts:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFactText("");
    setCategory("fun_fact");
    setSourceUrl("");
    setEditingFact(null);
  };

  const handleSave = async () => {
    if (!factText.trim()) return;
    
    setSaving(true);
    try {
      if (editingFact) {
        // Update existing
        await axios.put(`${API}/achievement-facts/admin/facts/${editingFact.id}`, {
          factText: factText.trim(),
          category,
          sourceUrl: sourceUrl.trim() || null,
        });
        toast.success("Fact updated");
      } else {
        // Create new
        await axios.post(`${API}/achievement-facts/admin/achievement/${achievementId}/facts`, {
          factText: factText.trim(),
          category,
          sourceUrl: sourceUrl.trim() || null,
        });
        toast.success("Fact added");
      }
      resetForm();
      setAddDialogOpen(false);
      fetchFacts();
    } catch (error) {
      console.error("Failed to save fact:", error);
      toast.error("Failed to save fact");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (factId) => {
    try {
      await axios.delete(`${API}/achievement-facts/admin/facts/${factId}`);
      toast.success("Fact deleted");
      fetchFacts();
    } catch (error) {
      console.error("Failed to delete fact:", error);
      toast.error("Failed to delete fact");
    }
  };

  const openEditDialog = (fact) => {
    setEditingFact(fact);
    setFactText(fact.factText);
    setCategory(fact.category);
    setSourceUrl(fact.sourceUrl || "");
    setAddDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-400" />
            Facts & Trivia
          </h3>
          <p className="text-sm text-white/50">{achievementName} • {gameName}</p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            resetForm();
            setAddDialogOpen(true);
          }}
          className="bg-amber-600 hover:bg-amber-500"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Fact
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      ) : facts.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-white/10 rounded-lg">
          <Lightbulb className="w-8 h-8 mx-auto text-white/20 mb-2" />
          <p className="text-white/40">No facts yet</p>
          <p className="text-white/30 text-sm">Click "Add Fact" to create one</p>
        </div>
      ) : (
        <div className="space-y-2">
          {facts.map((fact) => {
            const cat = getCategoryInfo(fact.category);
            return (
              <div
                key={fact.id}
                className="bg-black/30 rounded-lg p-3 border border-white/10 group"
              >
                <div className="flex items-start gap-2">
                  <span className="text-base">{cat.icon}</span>
                  <div className="flex-1 min-w-0">
                    <FormattedText text={fact.factText} className="text-white/90 text-sm" />
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                      <span>{cat.label}</span>
                      {fact.sourceUrl && (
                        <a
                          href={fact.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-white/60"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Source
                        </a>
                      )}
                      {fact.showSubmitter && fact.submittedByUsername && (
                        <span>by {fact.submittedByUsername}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openEditDialog(fact)}
                      className="h-7 w-7 text-white/40 hover:text-white"
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-400/60 hover:text-red-400"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Fact?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently remove this fact from the achievement.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(fact.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingFact ? "Edit Fact" : "Add New Fact"}
            </DialogTitle>
            <DialogDescription>
              {achievementName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Fact Text *</Label>
              <FormattingToolbar
                textareaRef={factTextareaRef}
                value={factText}
                onChange={setFactText}
              />
              <Textarea
                ref={factTextareaRef}
                placeholder="Enter the fact or trivia..."
                value={factText}
                onChange={(e) => setFactText(e.target.value)}
                className="bg-black/50 border-white/10 min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {FACT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <span className="flex items-center gap-2">
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Source URL (optional)</Label>
              <Input
                placeholder="https://..."
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className="bg-black/50 border-white/10"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setAddDialogOpen(false)}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!factText.trim() || saving}
                className="flex-1 bg-amber-600 hover:bg-amber-500"
              >
                {saving ? "Saving..." : editingFact ? "Update" : "Add Fact"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Indicator badge for achievements with pending fact submissions
 */
export function PendingFactBadge({ count }) {
  if (!count || count === 0) return null;
  
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30">
      <Lightbulb className="w-3 h-3" />
      {count}
    </span>
  );
}
